import { useMemo, useState } from "react";
import dayjs from "dayjs";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Skeleton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";

// ─── Data mapping (mirrors Conversions Upload Desktop) ───────────────────────
type SortKey = "date" | "ftd" | "qftd";
type SortDir = "asc" | "desc";

interface ConversionsUploadRow {
  date: string;
  dateValue: number | null;
  ftdLoaded: string;
  ftdTotal: string;
  ftdPercent: number | null;
  qftdLoaded: string;
  qftdTotal: string;
  qftdPercent: number | null;
}

// Keep raw values; dash when missing.
const raw = (value: any) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

// API date (e.g. "2026-07-11") → "DD-MM-YYYY", or "-" when invalid.
const formatDate = (value: any) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(value);
};

// Sortable timestamp for the upload date, or null when invalid.
const dateSortValue = (value: any): number | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

// Client-calculated percentage: (numerator / denominator) * 100, or null.
const percentValue = (numerator: any, denominator: any): number | null => {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!d || Number.isNaN(n) || Number.isNaN(d)) return null;
  return (n / d) * 100;
};
// ─────────────────────────────────────────────────────────────────────────────

// Badge color thresholds (mirror Conversions Upload Desktop):
// < 88 red, 88–92 amber, >= 93 green.
const getBadgeStyle = (percent: number) => {
  if (percent < 88) {
    return {
      bg: colors.Error[50].$value,
      color: colors.Error[600].$value,
      border: colors.Error[200].$value,
    };
  }
  if (percent < 93) {
    return {
      bg: colors.Warning[50].$value,
      color: colors.Warning[600].$value,
      border: colors.Warning[200].$value,
    };
  }
  return {
    bg: colors.Success[50].$value,
    color: colors.Success[600].$value,
    border: colors.Success[200].$value,
  };
};

const PercentBadge = ({ percent }: { percent: number }) => {
  const style = getBadgeStyle(percent);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        borderRadius: spacing.Radius.xs.$value,
        px: spacing.Spacing.xs.$value,
        fontSize: typography.Size.Text.sm.$value,
        fontWeight: typography.Font_weight.Medium.$value,
        lineHeight: typography.Line_height.text.sm.$value,
        whiteSpace: "nowrap",
      }}
    >
      {Math.round(percent)}%
    </Box>
  );
};

// Renders "loaded / total" with the percentage badge AFTER the values.
const LoadedCell = ({
  loaded,
  total,
  percent,
}: {
  loaded: string;
  total: string;
  percent: number | null;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: spacing.Spacing.sm.$value,
    }}
  >
    <Typography
      component="span"
      sx={{
        color: colors.GrayLightMode[900].$value,
        fontSize: typography.Size.Text.sm.$value,
        fontWeight: typography.Font_weight.Semibold.$value,
        whiteSpace: "nowrap",
      }}
    >
      {loaded} / {total}
    </Typography>
    {percent !== null && <PercentBadge percent={percent} />}
  </Box>
);

const headerCellSx = {
  color: colors.GrayLightMode[500].$value,
  fontSize: typography.Size.Text.xs.$value,
  fontWeight: typography.Font_weight.Medium.$value,
  borderBottom: "1px solid " + colors.GrayLightMode[200].$value,
  px: spacing.Spacing.sm.$value,
  whiteSpace: "nowrap",
} as const;

const bodyCellSx = {
  borderBottom: "1px solid " + colors.GrayLightMode[200].$value,
  px: spacing.Spacing.sm.$value,
  py: spacing.Spacing.md.$value,
} as const;

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "date", label: "" },
  { key: "ftd", label: "Loaded / Total FTD" },
  { key: "qftd", label: "Loaded / Total QFTD" },
];

const getSortValue = (row: ConversionsUploadRow, key: SortKey) => {
  if (key === "date") return row.dateValue;
  if (key === "ftd") return row.ftdPercent;
  return row.qftdPercent;
};

interface ConversionsUploadTableProps {
  data?: any[];
  isLoading?: boolean;
}

const ConversionsUploadTable = ({
  data = [],
  isLoading = false,
}: ConversionsUploadTableProps) => {
  // Default sort: newest upload date first (matches the design order).
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Map the API output to the three displayed columns (same keys as Desktop).
  const rows = useMemo<ConversionsUploadRow[]>(
    () =>
      (data ?? []).map((item: Record<string, any>) => ({
        date: formatDate(item.brand_action_date),
        dateValue: dateSortValue(item.brand_action_date),
        ftdLoaded: raw(item.FTD_loaded),
        ftdTotal: raw(item.brand_total_ftd),
        ftdPercent: percentValue(item.FTD_loaded, item.brand_total_ftd),
        qftdLoaded: raw(item.QFTD_loaded),
        qftdTotal: raw(item.Total_Qftd),
        qftdPercent: percentValue(item.QFTD_loaded, item.Total_Qftd),
      })),
    [data],
  );

  // Sort by the active column; null/missing values always sink to the bottom.
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aValue = getSortValue(a, sortKey);
      const bValue = getSortValue(b, sortKey);
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;
      if (aValue < bValue) return sortDir === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sortKey, sortDir]);

  return (
    <TableContainer sx={{ mt: spacing.Spacing.md.$value }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => {
              const isActive = sortKey === column.key;
              return (
                <TableCell
                  key={column.key}
                  align="left"
                  onClick={() => handleSort(column.key)}
                  sx={{
                    ...headerCellSx,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: spacing.Spacing.xs.$value,
                    }}
                  >
                    {column.label}
                    <ArrowDownwardIcon
                      sx={{
                        fontSize: typography.Size.Text.md.$value,
                        color: colors.GrayLightMode[700].$value,
                        opacity: isActive ? 1 : 0,
                        transform:
                          isActive && sortDir === "asc"
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        transition: "0.2s",
                      }}
                    />
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            [...Array(8)].map((_, index) => (
              <TableRow key={index}>
                {[0, 1, 2].map((cell) => (
                  <TableCell key={cell} align="left" sx={bodyCellSx}>
                    <Skeleton width={cell === 0 ? 70 : 110} height={45} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : sortedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{ py: 6, color: colors.GrayLightMode[500].$value }}
              >
                No data found
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row, index) => (
              <TableRow key={`${row.date}-${index}`}>
                <TableCell
                  align="left"
                  sx={{
                    ...bodyCellSx,
                    color: colors.GrayLightMode[700].$value,
                    fontSize: typography.Size.Text.sm.$value,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.date}
                </TableCell>
                <TableCell align="left" sx={bodyCellSx}>
                  <LoadedCell
                    loaded={row.ftdLoaded}
                    total={row.ftdTotal}
                    percent={row.ftdPercent}
                  />
                </TableCell>
                <TableCell align="left" sx={bodyCellSx}>
                  <LoadedCell
                    loaded={row.qftdLoaded}
                    total={row.qftdTotal}
                    percent={row.qftdPercent}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ConversionsUploadTable;
